// Copyright 2024 Sadiant Inc. All Rights Reserved. This software is subject to a license agreement. Unauthorized or unlicensed use is prohibited.
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppInsightsService } from '@sadiant/azure-app-insights';
import { Account } from '@sadiant/data-access';
import { Observable, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { RegisterService } from '../../modules/register/register.service';
import { AccountCoreApiService } from '../../shared/services/core-api/account-core-api.service';
import { IntercomService } from '../../shared/services/intercom.service';
import { ProfileService } from '../../shared/services/profile.service';
import { UserActionsService } from '../../store/actions';
import { TokenService } from './token.service';

import RefreshTokenIn = Account.RefreshToken.RefreshTokenIn;
import RefreshTokenOut = Account.RefreshToken.RefreshTokenOut;
import LoginIn = Account.Login.LoginIn;

@Injectable({ providedIn: 'root' })
export class AuthenticationCoreService {
  constructor(
    private accountCoreApiService: AccountCoreApiService,
    private tokenService: TokenService,
    private profileService: ProfileService,
    private router: Router,
    private userActionsService: UserActionsService,
    private appInsights: AppInsightsService,
    private intercom: IntercomService,
    private registerService: RegisterService
  ) {}

  login(request: LoginIn): Observable<void> {
    return this.accountCoreApiService.login(request).pipe(
      tap(loginResponse => this.tokenService.saveTokensToLocalStorage(loginResponse)),
      map(_ => {})
    );
  }

  getNewRefreshToken(): Observable<RefreshTokenOut> {
    const refreshToken = this.tokenService.getRefreshTokenFromLocalStorage();
    const request: RefreshTokenIn = {
      tokenValue: refreshToken?.token ?? '',
      tokenId: refreshToken?.tokenId ?? 0
    };
    if (!request.tokenValue || !request.tokenId) {
      return throwError('Invalid Token Value');
    } else {
      return this.accountCoreApiService
        .getRefreshToken(request)
        .pipe(tap(response => this.tokenService.saveTokensToLocalStorage(response)));
    }
  }

  logout(isRedirectWithOutError: boolean = true): void {
    const refreshToken = this.tokenService.getRefreshTokenFromLocalStorage();
    const request: RefreshTokenIn = {
      tokenValue: refreshToken?.token ?? '',
      tokenId: refreshToken?.tokenId ?? 0
    };
    if (request.tokenId && request.tokenValue) {
      this.accountCoreApiService.logOutRevokeToken(request).subscribe();
    }

    this.tokenService.deleteTokensFromLocalStorage();
    this.profileService.setProfile(null);
    this.clearAllCachedInfoFromStateStore();
    this.appInsights.clearUserId();
    this.intercom.shutdown();
    this.registerService.resetRegistration();
    if (isRedirectWithOutError) {
      this.router.navigate(['/Login']);
    }
  }

  private clearAllCachedInfoFromStateStore(): void {
    this.userActionsService.clearUser();
  }
}
